import { ComponentColor } from "@common/types";
import { LocationStatus } from "@models/location";
import {
    LocationActivationOutcome,
    type LocationApprovalResult,
} from "@models/organization";

export type ApprovalOutcomeKind =
    | "approved"
    | "declined"
    | "unavailable"
    | "unconfirmed"
    | "waitingForPayment"
    | "noCertificate"
    | "unseenCertificate"
    | "notApproved";

export interface ApprovalOutcomeView {
    kind: ApprovalOutcomeKind;
    // The ONE approved rule: the summary count and the row badge both read it.
    isApproved: boolean;
    label: string;
    // Success for an approval, Warning for a location that simply was not
    // charged, Error for a failed or unknown charge.
    color: ComponentColor;
    // What the admin needs to know or do; null for a plain approval.
    message: string | null;
}

const NOT_APPROVED: ApprovalOutcomeView = {
    kind: "notApproved",
    isApproved: false,
    label: "Not approved",
    color: ComponentColor.Error,
    message: "Refresh to see this location's current status.",
};

// Substring of the API's error for a certificate uploaded at or after
// certificatesSeenAt (ApproveOrganizationCommand); the missing-certificate
// error carries different text.
const UNSEEN_CERTIFICATE = "uploaded since you opened this client";

// What one approval result means to the admin (#434). A location that did not
// end up Approved must never read as a success, so this fails CLOSED: only the
// API's `approved` flag AND an Approved status together make an approval, and
// a pending-payment location with an outcome this UI does not know reads as
// "Not approved", not as anything benign.
export const describeApprovalResult = (
    result: LocationApprovalResult,
): ApprovalOutcomeView => {
    if (result.approved && result.status === LocationStatus.Approved) {
        return {
            kind: "approved",
            isApproved: true,
            label: "Approved",
            color: ComponentColor.Success,
            message: null,
        };
    }

    // #437: a Pending location is skipped — not reviewed, not charged — and
    // stays Pending when it has no resale certificate, or (PR 327 round 5)
    // when one was uploaded after the admin's page loaded. Those are the only
    // results the API leaves Pending, with the same shape; its error is what
    // tells them apart. Nothing failed for either, so a warning, never a
    // payment one.
    if (
        !result.approved &&
        result.status === LocationStatus.Pending &&
        result.activationOutcome === LocationActivationOutcome.NotAttempted
    ) {
        if (result.error?.includes(UNSEEN_CERTIFICATE)) {
            return {
                kind: "unseenCertificate",
                isApproved: false,
                label: "New certificate — review it before approving",
                color: ComponentColor.Warning,
                message:
                    "A new resale certificate was uploaded since this page loaded. Review it, then approve again.",
            };
        }

        return {
            kind: "noCertificate",
            isApproved: false,
            label: "Not approved: no resale certificate",
            color: ComponentColor.Warning,
            message:
                "The customer must upload this location's resale certificate before it can be approved.",
        };
    }

    if (result.status !== LocationStatus.PendingPayment) {
        return NOT_APPROVED;
    }

    switch (result.activationOutcome) {
        case LocationActivationOutcome.Declined:
            return {
                kind: "declined",
                isApproved: false,
                label: "Card declined",
                color: ComponentColor.Error,
                message:
                    "Card declined, customer emailed, waiting for a new card.",
            };

        case LocationActivationOutcome.Unavailable:
            return {
                kind: "unavailable",
                isApproved: false,
                label: "Payment not attempted",
                color: ComponentColor.Error,
                message:
                    "Payment couldn't be attempted, try approving again later.",
            };

        case LocationActivationOutcome.Unconfirmed:
            return {
                kind: "unconfirmed",
                isApproved: false,
                label: "Charge unconfirmed",
                color: ComponentColor.Error,
                message:
                    "Charge unconfirmed, resolve it before approving again.",
            };

        // No charge was tried for this location: an earlier one in the same
        // approval did not activate, or the client's membership is paid up but
        // not chargeable right now. Nothing failed for it, so a warning. Either
        // way the customer's next card save or the next successful renewal
        // charges it and completes the approval.
        case LocationActivationOutcome.NotAttempted:
            return {
                kind: "waitingForPayment",
                isApproved: false,
                label: "Waiting for payment",
                color: ComponentColor.Warning,
                message:
                    "Not charged yet. It's approved when a charge goes through: the customer's next card save or the next membership renewal.",
            };

        default:
            return NOT_APPROVED;
    }
};
